import { useFetch, useSubmit } from "@hyper-fetch/react";
import { Banknote, Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { sdk } from "@/api/sdk";
import { surface } from "@/components/layouts/shells/shell-shared";
import { Button } from "@/components/ui/button";
import { useBookingClient } from "@/features/booking/hooks/use-booking-client";
import { money } from "@/features/payments/utils/format";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

const when = (iso: string) =>
  new Intl.DateTimeFormat(getLocale(), {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

/**
 * Visits the client has to pay ahead, soonest deadline first, each with its way to pay: online
 * straight away, or the business's account for a transfer. Shows nothing when nothing is due.
 */
export function DuePayments({ onPay = (url) => window.location.assign(url) }: { onPay?: (url: string) => void }) {
  const { query } = useBookingClient();
  const { data } = useFetch(sdk.me.payments.due.$get.setQueryParams(query), {
    dependencies: [query.organizationId ?? ""],
  });
  const pay = useSubmit(sdk.me.payments.visits.$bookingId.pay.$post);
  const [transfer, setTransfer] = useState<{ bookingId: string; account: string | null; business: string } | null>(
    null,
  );

  if (!data || data.length === 0) return null;

  const start = async (bookingId: string, occurrenceStart: string) => {
    const { data: info, error } = await pay.submit({
      params: { bookingId },
      queryParams: query,
      payload: { occurrenceStart },
    });
    if (error || !info) {
      toast.error(error?.message || m.due_payments_failed());
      return;
    }
    if (info.payUrl) {
      onPay(info.payUrl);
      return;
    }
    setTransfer({ bookingId, account: info.bankAccount, business: info.businessName });
  };

  return (
    <section className="grid gap-2" aria-labelledby="due-payments-title">
      <h2 id="due-payments-title" className="flex items-center gap-2 text-sm font-medium">
        <Banknote className="size-4 text-primary" aria-hidden />
        {m.due_payments_title()}
      </h2>
      <ul className={cn(surface, "divide-y overflow-hidden")}>
        {data.map((visit) => (
          <li key={`${visit.bookingId}-${visit.occurrenceStart}`} className="grid gap-2 px-4 py-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{visit.title}</p>
                <p className="text-xs text-muted-foreground">
                  {m.due_payments_visit({ visit: when(visit.occurrenceStart), due: when(visit.dueAt) })}
                  {visit.cancelIfUnpaid ? ` · ${m.due_payments_cancel()}` : ""}
                </p>
              </div>
              <span className="text-sm font-semibold tabular-nums">{money(visit.amount, visit.currency)}</span>
              <Button
                type="button"
                size="sm"
                onClick={() => void start(visit.bookingId, visit.occurrenceStart)}
                disabled={pay.submitting}
              >
                {m.due_payments_pay()}
              </Button>
            </div>
            {transfer?.bookingId === visit.bookingId ? (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
                {transfer.account ? (
                  <>
                    <span className="text-muted-foreground">
                      {m.booking_portal_prepay_transfer({ business: transfer.business })}
                    </span>
                    <span className="font-mono">{transfer.account}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      aria-label={m.booking_portal_copy_account()}
                      onClick={() =>
                        void navigator.clipboard
                          ?.writeText(transfer.account ?? "")
                          .then(() => toast.success(m.booking_portal_copied()))
                      }
                    >
                      <Copy />
                    </Button>
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    {m.booking_portal_prepay_ask({ business: transfer.business })}
                  </span>
                )}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
